import { createError } from '@common/neo.tax';

import {
  AccountingQreFilter,
  AccountingQreSort,
  GetAccountingQresQuery,
  GetAccountingQresQueryVariables,
  GET_ACCOUNTING_QRES,
  useCursorPaginatedQuery,
  useSticky,
  Conditions,
  advancedFilter,
  FilterField,
  keywordFilter,
} from '@neo.tax/shared';

import { useRdContext } from '../context';

export type AccountingQre = NonNullable<GetAccountingQresQuery['getAccountingQres']['edges']>[number]['node'];

const GET_ACCOUNTING_QRES_KEY = 'GET_ACCOUNTING_QRES_KEY';

interface UseAccountingQresProps {
  filterConditions?: Conditions<FilterField<AccountingQreFilter>>;
  searchKeyword?: string | null;
  sorting?: Array<AccountingQreSort> | AccountingQreSort;
}

export const useAccountingQres = ({ filterConditions, searchKeyword, sorting = [] }: UseAccountingQresProps = {}): {
  loading: boolean;
  accountingQres: AccountingQre[] | null;
  hasNextPage: boolean;
  onLoadMoreItems: () => Promise<void>;
  isLoadingMore: boolean;
} => {
  const { fiscalYearId } = useRdContext();

  const {
    data: requestData,
    error,
    hasNextPage,
    isLoadingMore,
    loading,
    onLoadMoreItems,
  } = useCursorPaginatedQuery<GetAccountingQresQuery, GetAccountingQresQueryVariables>(
    GET_ACCOUNTING_QRES,
    {
      variables: {
        filter: {
          and: [keywordFilter(['name'], searchKeyword), advancedFilter<AccountingQreFilter>(filterConditions)],
        },
        sorting,
        fiscalYearId,
      },
      context: {
        debounceKey: GET_ACCOUNTING_QRES_KEY,
      },
    },
    {
      getConnection: (data) => data.getAccountingQres,
      numElementsPerFetch: 40,
    },
  );
  // Keep existing data on the screen while a new query is running, so it
  // doesn't keep clearing the screen as you type into the filter.
  const stickyData = useSticky(requestData);
  const accountingQres = stickyData?.getAccountingQres?.edges.map((edge) => edge.node) ?? null;

  if (loading) {
    return {
      loading: true,
      accountingQres: accountingQres,
      hasNextPage: false,
      onLoadMoreItems: () => Promise.resolve(),
      isLoadingMore,
    };
  }

  if (error) {
    throw error;
  }
  if (!stickyData) {
    throw createError('MissingAccountingQres', 'No data returned from getAccountingQres query');
  }

  return {
    loading,
    accountingQres,
    hasNextPage,
    onLoadMoreItems,
    isLoadingMore,
  };
};
